import type { Metro, OccupationGroupAlignment } from '../types/metro';
import type {
  MetroCountsResponse,
  OgCountsResponse,
  ProvidersResponse,
} from '../types/provider';
import { getStatus } from '../lib/utils';

/**
 * API client for the CEW Data API (see backend/).
 *
 * Base URL comes from VITE_API_BASE_URL (set in `.env.development` for local
 * dev, in CI/CD for production). The client throws ApiError on any non-2xx
 * response or unexpected payload shape — callers handle errors via the
 * useMetroData / useProviders hooks.
 *
 * E09 / DEC-049 — API access control:
 * Every request to `/api/*` carries an `X-API-Key` header sourced from
 * `VITE_API_KEY` (read once at module load, NOT per-fetch). The key is
 * baked into the bundled JS at build time. `scripts/check-env-for-prod-build.mjs`
 * fails the production build if the var is unset, so a misconfigured deploy
 * doesn't ship a 401-loop frontend.
 *
 * If the value is empty in development, the API returns 401 and the user
 * sees the standard ApiError flow — same as a misconfigured prod build,
 * but recoverable by setting the var and rebuilding.
 *
 * Two fetch helpers:
 *   - `get<T>()`     — for endpoints that return `{ success, data }`
 *                       envelopes (e.g. /api/metros). Unwraps to `T`.
 *   - `getRaw<T>()`  — for endpoints that return the body directly
 *                       (e.g. /api/providers — DEC-057, see backend
 *                       schemas/provider.py for the rationale).
 */

const BASE_URL = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://127.0.0.1:8000'
).replace(/\/+$/, '');

// Read once at module load so every fetch uses the same value without
// re-touching import.meta.env. Empty string when unset — backend will
// 401, ApiError surfaces, no per-fetch overhead.
const API_KEY = (import.meta.env.VITE_API_KEY as string | undefined) ?? '';

const REQUEST_HEADERS: HeadersInit = {
  Accept: 'application/json',
  // E09: every request carries the API key. Header name matches
  // the backend's middleware/auth.py expectation exactly.
  'X-API-Key': API_KEY,
};

interface SuccessEnvelope<T> {
  readonly success: true;
  readonly data: T;
}

interface ErrorEnvelope {
  readonly success: false;
  readonly error: { readonly code: string; readonly message: string };
}

type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

/** Thrown for any API failure (network, non-2xx, or non-success envelope). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Shared fetch helper for `get<T>()` and `getRaw<T>()`.
 *
 * Centralizes the NETWORK_ERROR mapping so any network failure
 * surfaces with the same error code + message shape regardless of
 * which envelope the caller expects.
 *
 * Returns the raw Response — body parsing stays in the caller because
 * `get<T>()` and `getRaw<T>()` use different strategies (envelope
 * unwrapping vs. middleware-friendly optimistic parse).
 */
async function doFetch(path: string): Promise<Response> {
  try {
    return await fetch(`${BASE_URL}${path}`, { headers: REQUEST_HEADERS });
  } catch {
    throw new ApiError('NETWORK_ERROR', `Cannot reach API at ${BASE_URL}${path}`, 0);
  }
}

async function get<T>(path: string): Promise<T> {
  const response = await doFetch(path);

  let body: ApiEnvelope<T>;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError('BAD_RESPONSE', 'API returned non-JSON response', response.status);
  }

  if (!response.ok || body.success === false) {
    const err = body.success === false ? body.error : { code: 'HTTP_ERROR', message: response.statusText };
    throw new ApiError(err.code, err.message, response.status);
  }
  return body.data;
}

/**
 * Sibling of `get<T>()` that skips envelope unwrapping. Used for the
 * /api/providers endpoint which returns its body directly (DEC-057):
 *
 *   { items: [...], total, offset, limit }
 *
 * Same auth header + same NETWORK_ERROR / BAD_RESPONSE / HTTP_ERROR
 * shape on failure, so callers can use one error-handling path.
 *
 * Error-body parsing (parity with `get<T>()` — fixes the WARN #1
 * envelope mismatch where middleware-rejected requests surfaced as
 * generic HTTP_ERROR instead of UNAUTHORIZED / FORBIDDEN_ORIGIN /
 * RATE_LIMITED):
 *
 *   1. Try to parse the body as JSON.
 *   2. If it matches the success-envelope shape
 *      `{ success: false, error: { code, message } }` — emitted by
 *      `backend/middleware/{auth,origin_check,rate_limit}.py` on
 *      401/403/429 — surface the structured code + message.
 *   3. Else if the body has FastAPI's `{ detail: ... }` shape (e.g.
 *      422 validation errors from the route handler), surface that.
 *   4. Otherwise fall back to a generic HTTP_ERROR with status text.
 */
async function getRaw<T>(path: string): Promise<T> {
  const response = await doFetch(path);

  // Parse the body as JSON once up-front. Success path AND error path
  // both want the parsed body; reading the stream twice would throw.
  let body: unknown;
  let bodyParsed = false;
  try {
    body = await response.json();
    bodyParsed = true;
  } catch {
    // body wasn't JSON — fall through; non-2xx uses statusText,
    // 2xx falls into BAD_RESPONSE below.
  }

  if (!response.ok) {
    // (1) Middleware envelope: { success: false, error: { code, message } }
    if (
      bodyParsed &&
      typeof body === 'object' &&
      body !== null &&
      (body as { success?: unknown }).success === false &&
      typeof (body as { error?: unknown }).error === 'object' &&
      (body as { error: unknown }).error !== null
    ) {
      const err = (body as ErrorEnvelope).error;
      if (typeof err.code === 'string' && typeof err.message === 'string') {
        throw new ApiError(err.code, err.message, response.status);
      }
    }
    // (2) FastAPI HTTPException: { detail: string | { message } }
    let detail = response.statusText;
    if (bodyParsed && typeof body === 'object' && body !== null) {
      const d = (body as { detail?: unknown }).detail;
      if (typeof d === 'string') {
        detail = d;
      } else if (
        d !== null &&
        typeof d === 'object' &&
        typeof (d as { message?: unknown }).message === 'string'
      ) {
        detail = (d as { message: string }).message;
      }
    }
    throw new ApiError('HTTP_ERROR', detail, response.status);
  }

  if (!bodyParsed) {
    throw new ApiError('BAD_RESPONSE', 'API returned non-JSON response', response.status);
  }
  return body as T;
}

/**
 * Re-derive `status` from `ratio` at the API boundary.
 *
 * DEC-056 (FINAL) collapsed AlignmentStatus to a binary cut at 1.0.
 * The backend may still emit the legacy 'balanced' string until the
 * P2-D ingest cleanup lands; normalize here so every consumer sees a
 * value that satisfies the narrowed type. We re-derive instead of
 * just dropping 'balanced' so the field stays consistent with `ratio`
 * even on payloads that pre-date the binary contract.
 *
 * Also re-derives nested OccupationGroupAlignment statuses for the
 * same reason — `getSectorData()` falls back to seed-math for older
 * fixtures but real data carries `occupationGroups` straight through.
 */
function normalizeMetro(raw: Metro): Metro {
  const occupationGroups = raw.occupationGroups
    ? raw.occupationGroups.map(
        (g): OccupationGroupAlignment => ({ ...g, status: getStatus(g.ratio) }),
      )
    : raw.occupationGroups;
  return { ...raw, status: getStatus(raw.ratio), occupationGroups };
}

/**
 * Query parameters accepted by `api.providers.list()`. Mirrors the
 * Query() args in `backend/routers/providers.py::list_providers`.
 *
 * Note: `groupId` is the occupational group's full display NAME
 * (e.g. "Computer and mathematical sciences"), NOT a slug — the
 * backend matches it against entries of `Provider.occupationGroups`,
 * which carry the raw name. Callers that have a chip slug must
 * resolve it via `lookupSectorByChipId()` first.
 */
export interface ProvidersListParams {
  readonly search?: string;
  readonly groupId?: string;
  readonly metroId?: string;
  readonly limit?: number;
  readonly offset?: number;
}

/** Public API surface — keep methods narrow and typed. */
export const api = {
  metros: {
    list: (): Promise<Metro[]> =>
      get<Metro[]>('/api/metros').then((ms) => ms.map(normalizeMetro)),
    get: (id: string): Promise<Metro> =>
      get<Metro>(`/api/metros/${encodeURIComponent(id)}`).then(normalizeMetro),
  },
  providers: {
    /**
     * Paginated, filterable list of education institutions.
     *
     * Defaults: `limit=50`, `offset=0`. Filters intersect (AND
     * semantics). Backend returns items pre-sorted by totalAwards
     * desc; this client does not re-sort.
     */
    list: (params: ProvidersListParams = {}): Promise<ProvidersResponse> => {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.groupId) qs.set('groupId', params.groupId);
      if (params.metroId) qs.set('metroId', params.metroId);
      qs.set('limit', String(params.limit ?? 50));
      qs.set('offset', String(params.offset ?? 0));
      return getRaw<ProvidersResponse>(`/api/providers?${qs.toString()}`);
    },
    /**
     * Per-OG provider counts under the current filter set.
     *
     * Powers the right-aligned CountBadges next to each option in the
     * Providers OG combobox. 2026-06-05 (Maria): now also accepts the
     * `search` query so counts stay in sync with all active filters,
     * not just metroId. `metroId` is optional — omit for counts
     * spanning all metros (the "All metros" sentinel state).
     *
     * Returns ALL 22 canonical OGs, including zero-count buckets so
     * the frontend can render "(0)" badges (zero-count options stay
     * clickable; picking one narrows the table to its empty state).
     */
    ogCounts: (
      args: { metroId?: string; search?: string } = {},
    ): Promise<OgCountsResponse> => {
      const qs = new URLSearchParams();
      if (args.metroId) qs.set('metroId', args.metroId);
      if (args.search) qs.set('search', args.search);
      const suffix = qs.toString();
      return getRaw<OgCountsResponse>(
        `/api/providers/og-counts${suffix ? `?${suffix}` : ''}`,
      );
    },
    /**
     * Per-metro provider counts under the current filter set
     * (2026-06-05). Sibling of `ogCounts` — instead of per-OG counts
     * within an optional metro, this returns per-metro counts under
     * an optional `groupId` + `search`. Powers the right-aligned
     * CountBadges on the Providers Metro Area combobox.
     */
    metroCounts: (
      args: { groupId?: string; search?: string } = {},
    ): Promise<MetroCountsResponse> => {
      const qs = new URLSearchParams();
      if (args.groupId) qs.set('groupId', args.groupId);
      if (args.search) qs.set('search', args.search);
      const suffix = qs.toString();
      return getRaw<MetroCountsResponse>(
        `/api/providers/metro-counts${suffix ? `?${suffix}` : ''}`,
      );
    },
  },
};
